'use client';

import { c as _c } from "react/compiler-runtime";
import { FormGroup, FormInputErrors, FormLabel } from '~/components/primitives';
import { Input } from '~/components/primitives';
import { Checkbox } from '~/components/primitives';
import type { I18n } from '@funkey/locales';
import { z } from 'zod';
import { useI18n } from '~/features/i18n';
import { usePortalCtx } from '~/features/global/portal';
import type { FormState } from '~/modules/form';
import Title from '~/components/utils/Title';
import type { Auth_Session } from '~/modules/auth/shared';
export namespace CheckoutAccountFormFields {
  export type FormValues = {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    businessName: string;
    vatNumber: string;
    optinSubscribeToNewsletter?: boolean;
  };
  export function defaultValues(session: Pick<Auth_Session['user'], 'firstName' | 'lastName' | 'email' | 'phone'> | null): FormValues {
    return {
      // [Contact info]
      firstName: session?.firstName ?? '',
      lastName: session?.lastName ?? '',
      email: session?.email ?? '',
      phone: session?.phone ?? '',
      // [Customer info]
      businessName: '',
      vatNumber: '',
      // [Meta]
      optinSubscribeToNewsletter: false
    };
  }
  export const FieldSet: React.FC<{
    formState: FormState.Subform<CheckoutAccountFormFields.FormValues>;
    hiddenFields?: Array<'optinSubscribeToNewsletter' | 'businessName' | 'vatNumber'>;
  }> = t0 => {
    const $ = _c(95);
    const {
      formState,
      hiddenFields
    } = t0;
    const i18n = useI18n();
    const portal = usePortalCtx();
    let t1;
    if ($[0] !== i18n) {
      t1 = i18n._("Contact information");
      $[0] = i18n;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <Title as="legend" className="mb-10">{t1}</Title>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== i18n) {
      t3 = i18n._("First name");
      $[4] = i18n;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== i18n) {
      t4 = i18n._("First name");
      $[6] = i18n;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== formState) {
      t5 = formState.getNativeInputProps("firstName");
      $[8] = formState;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t4 || $[11] !== t5) {
      t6 = <Input placeholder={t4} id="firstName" required={true} type="text" {...t5} />;
      $[10] = t4;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== formState) {
      t7 = formState.getErrorMessages("firstName");
      $[13] = formState;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== t7) {
      t8 = <FormInputErrors errors={t7} />;
      $[15] = t7;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    let t9;
    if ($[17] !== t3 || $[18] !== t6 || $[19] !== t8) {
      t9 = <FormGroup htmlFor="firstName" label={t3} variant="no_spacing" required={true}>{t6}{t8}</FormGroup>;
      $[17] = t3;
      $[18] = t6;
      $[19] = t8;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== i18n) {
      t10 = i18n._("Last name");
      $[21] = i18n;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== i18n) {
      t11 = i18n._("Last name");
      $[23] = i18n;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    let t12;
    if ($[25] !== formState) {
      t12 = formState.getNativeInputProps("lastName");
      $[25] = formState;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    let t13;
    if ($[27] !== t11 || $[28] !== t12) {
      t13 = <Input placeholder={t11} id="lastName" {...t12} />;
      $[27] = t11;
      $[28] = t12;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== formState) {
      t14 = formState.getErrorMessages("lastName");
      $[30] = formState;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== t14) {
      t15 = <FormInputErrors errors={t14} />;
      $[32] = t14;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    let t16;
    if ($[34] !== t10 || $[35] !== t13 || $[36] !== t15) {
      t16 = <FormGroup htmlFor="lastName" label={t10} variant="no_spacing" required={true}>{t13}{t15}</FormGroup>;
      $[34] = t10;
      $[35] = t13;
      $[36] = t15;
      $[37] = t16;
    } else {
      t16 = $[37];
    }
    let t17;
    if ($[38] !== i18n) {
      t17 = i18n._("Email");
      $[38] = i18n;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    let t18;
    if ($[40] !== i18n) {
      t18 = i18n._("Email");
      $[40] = i18n;
      $[41] = t18;
    } else {
      t18 = $[41];
    }
    let t19;
    if ($[42] !== formState) {
      t19 = formState.getNativeInputProps("email");
      $[42] = formState;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    let t20;
    if ($[44] !== t18 || $[45] !== t19) {
      t20 = <Input placeholder={t18} id="email" {...t19} />;
      $[44] = t18;
      $[45] = t19;
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    let t21;
    if ($[47] !== formState) {
      t21 = formState.getErrorMessages("email");
      $[47] = formState;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    let t22;
    if ($[49] !== t21) {
      t22 = <FormInputErrors errors={t21} />;
      $[49] = t21;
      $[50] = t22;
    } else {
      t22 = $[50];
    }
    let t23;
    if ($[51] !== t17 || $[52] !== t20 || $[53] !== t22) {
      t23 = <FormGroup htmlFor="email" label={t17} variant="no_spacing" required={true}>{t20}{t22}</FormGroup>;
      $[51] = t17;
      $[52] = t20;
      $[53] = t22;
      $[54] = t23;
    } else {
      t23 = $[54];
    }
    let t24;
    if ($[55] !== i18n) {
      t24 = i18n._("Phone");
      $[55] = i18n;
      $[56] = t24;
    } else {
      t24 = $[56];
    }
    let t25;
    if ($[57] !== i18n) {
      t25 = i18n._("Phone");
      $[57] = i18n;
      $[58] = t25;
    } else {
      t25 = $[58];
    }
    let t26;
    if ($[59] !== formState) {
      t26 = formState.getNativeInputProps("phone");
      $[59] = formState;
      $[60] = t26;
    } else {
      t26 = $[60];
    }
    let t27;
    if ($[61] !== t25 || $[62] !== t26) {
      t27 = <Input placeholder={t25} id="Phone" {...t26} />;
      $[61] = t25;
      $[62] = t26;
      $[63] = t27;
    } else {
      t27 = $[63];
    }
    let t28;
    if ($[64] !== formState) {
      t28 = formState.getErrorMessages("phone");
      $[64] = formState;
      $[65] = t28;
    } else {
      t28 = $[65];
    }
    let t29;
    if ($[66] !== t28) {
      t29 = <FormInputErrors errors={t28} />;
      $[66] = t28;
      $[67] = t29;
    } else {
      t29 = $[67];
    }
    let t30;
    if ($[68] !== t24 || $[69] !== t27 || $[70] !== t29) {
      t30 = <FormGroup htmlFor="Phone" label={t24} variant="no_spacing" required={true}>{t27}{t29}</FormGroup>;
      $[68] = t24;
      $[69] = t27;
      $[70] = t29;
      $[71] = t30;
    } else {
      t30 = $[71];
    }
    let t31;
    if ($[72] !== formState || $[73] !== hiddenFields || $[74] !== i18n || $[75] !== portal) {
      t31 = !hiddenFields?.includes("businessName") && <FormGroup htmlFor="businessName" label={i18n._("Company name")} variant="no_spacing" required={true}><Input placeholder={i18n._("Company name")} id="businessName" readOnly={portal.portal === "BIZZ"} {...formState.getNativeInputProps("businessName")} /><FormInputErrors errors={formState.getErrorMessages("businessName")} /></FormGroup>;
      $[72] = formState;
      $[73] = hiddenFields;
      $[74] = i18n;
      $[75] = portal;
      $[76] = t31;
    } else {
      t31 = $[76];
    }
    let t32;
    if ($[77] !== formState || $[78] !== hiddenFields || $[79] !== i18n || $[80] !== portal) {
      t32 = !hiddenFields?.includes("vatNumber") && <FormGroup htmlFor="vatNumber" label={i18n._("VAT number")} variant="no_spacing"><Input placeholder={i18n._("VAT number")} id="vatNumber" readOnly={portal.portal === "BIZZ"} {...formState.getNativeInputProps("vatNumber")} /><FormInputErrors errors={formState.getErrorMessages("vatNumber")} /></FormGroup>;
      $[77] = formState;
      $[78] = hiddenFields;
      $[79] = i18n;
      $[80] = portal;
      $[81] = t32;
    } else {
      t32 = $[81];
    }
    let t33;
    if ($[82] !== formState || $[83] !== hiddenFields || $[84] !== i18n) {
      t33 = !hiddenFields?.includes("optinSubscribeToNewsletter") && <FormGroup variant="no_spacing"><div className="flex items-center gap-3"><Checkbox type="checkbox" id="optinSubscribeToNewsletter" checked={formState.values.optinSubscribeToNewsletter ?? false} onChange={() => formState.handleChange("optinSubscribeToNewsletter", !formState.values.optinSubscribeToNewsletter)} /><FormLabel label={i18n._("I would like to receive the Funkey newsletter")} htmlFor="optinSubscribeToNewsletter" /></div><FormInputErrors errors={formState.getErrorMessages("optinSubscribeToNewsletter")} /></FormGroup>;
      $[82] = formState;
      $[83] = hiddenFields;
      $[84] = i18n;
      $[85] = t33;
    } else {
      t33 = $[85];
    }
    let t34;
    if ($[86] !== t16 || $[87] !== t2 || $[88] !== t23 || $[89] !== t30 || $[90] !== t31 || $[91] !== t32 || $[92] !== t33 || $[93] !== t9) {
      t34 = <fieldset className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="checkout-account-form">{t2}{t9}{t16}{t23}{t30}{t31}{t32}{t33}</fieldset>;
      $[86] = t16;
      $[87] = t2;
      $[88] = t23;
      $[89] = t30;
      $[90] = t31;
      $[91] = t32;
      $[92] = t33;
      $[93] = t9;
      $[94] = t34;
    } else {
      t34 = $[94];
    }
    return t34;
  };
  export const FORM_VALIDATION = (i18n: I18n): FormState.Validation<CheckoutAccountFormFields.FormValues> => ({
    businessName: {
      validate: value => VALIDATORS.businessName.safeParse(value).success,
      message: i18n._('Business name is required')
    },
    firstName: {
      validate: value => NAME_VALIDATOR.safeParse(value).success,
      message: i18n._('First name is required')
    },
    lastName: {
      validate: value => NAME_VALIDATOR.safeParse(value).success,
      message: i18n._('Last name is required')
    },
    email: {
      validate: value => z.email().safeParse(value).success,
      message: i18n._('Email address is required')
    },
    phone: {
      validate: value => NAME_VALIDATOR.min(6).safeParse(value).success,
      message: i18n._('Phone is required')
    }
  });
  const NAME_VALIDATOR = z.string().min(2).max(256).trim();
  export const VALIDATORS = {
    firstName: NAME_VALIDATOR,
    lastName: NAME_VALIDATOR,
    phone: NAME_VALIDATOR,
    email: NAME_VALIDATOR,
    businessName: NAME_VALIDATOR
  } as const satisfies { [K in keyof FormValues]?: z.ZodTypeAny };
}